'use client';

import { ClubCard } from '@pickleballinc/features/src/global/cards/ClubCardv2';
import { formatLocation } from '@pickleballinc/lib/format-location';
import { Session } from '@pickleballinc/lib/session';
import { PaginationV2 } from '@pickleballinc/react-ui';
import { MyClubCard } from '@pickleballinc/ui/components/global/Cards/MyClubCard';
import { MyModulesNoResults } from '@pickleballinc/ui/components/global/myModules/MyModulesNoResults';
import CustomLink from '@pickleballinc/ui/components/ui/custom-link';
import { useSearchParams } from 'next/navigation';
import { useRouter } from 'next/navigation';

import { useClubFavorite } from '@/hooks/tourney/useFavoriteToggle';
import { publicEnv } from '@/lib/env';
import AccessLimited from '@/modules/players/v2/tabs/components/AccessLimited';
import {
  ALL_MODULES_PAGE_SIZE,
  ORGANIZATION_MODULE_FOR,
} from '@/modules/players/v2/tabs/constants';
import { getNoResultsConfig } from '@/modules/players/v2/tabs/helpers';
import { useAuth } from '@/permissions/AuthProvider';
import { Permission } from '@/permissions/permission';
import { clubTypeMap, ClubTypes, RenderedPlayerClubsData } from '@/types/clubs.types';
import { PlayerData } from '@/types/players.types';
import { useFavoriteLeagueMutation } from '@/utils/actions/leagues/useFavoriteLeagueMutation';
import { FEATURE_FLAGS } from '@/utils/helpers/constants';

interface ClubsWrapperProps {
  clubs: RenderedPlayerClubsData[] | undefined;
  player: PlayerData | undefined;
  user?: Session;
  isFavoritesTab: boolean;
  totalRecords: number;
}

export const ClubsWrapper = ({
  clubs,
  player,
  user,
  isFavoritesTab,
  totalRecords,
}: ClubsWrapperProps) => {
  const router = useRouter();
  const { hasPermission } = useAuth();
  const isPickleballSuperAdmin = hasPermission(Permission.SUPER_ADMIN);
  const isPBSuperAdmin = hasPermission(Permission.PICKLEBALL_BRACKETS_ADMIN);
  const isSuperAdmin = isPBSuperAdmin || isPickleballSuperAdmin;
  const isUserAlsoPlayer = user?.pbUuid?.toLowerCase() === player?.pbUuid?.toLowerCase();

  const { addToFavorites, removeFromFavorites, isAdding, isRemoving } = useFavoriteLeagueMutation();

  const searchParams = useSearchParams();
  const page = Number(searchParams?.get('page') || 1);
  const searchParamForClub = searchParams?.get('club_type') || 'memberof';

  const currentClubType = clubTypeMap[searchParamForClub];

  const isManage = currentClubType === ClubTypes.Managing;
  const isMemberOf = currentClubType === ClubTypes.MemberOf;
  const isFavorites = currentClubType === ClubTypes.Favorites;

  const canManageClub = isManage && (isUserAlsoPlayer || isSuperAdmin);
  const hasClubs = clubs && clubs.length > 0;

  const noResultsConfig = getNoResultsConfig({
    moduleType: 'club',
    isManage,
    isRegistered: isMemberOf,
    player,
    isUserAlsoPlayer,
  });

  const handleFavoriteToggle = (
    event: React.MouseEvent,
    id: string,
    isFavourite?: boolean | null
  ) => {
    event.stopPropagation();
    event.preventDefault();

    const mutation = isFavourite ? removeFromFavorites : addToFavorites;

    if (isAdding || isRemoving) return;

    mutation.mutate(
      { moduleUuid: id, moduleFor: ORGANIZATION_MODULE_FOR },
      {
        onSuccess: () => {
          router.refresh();
        },
      }
    );
  };

  return (
    <div className='flex flex-col items-center gap-6 sm:gap-10 justify-center'>
      {hasClubs && (isMemberOf || canManageClub || isFavorites) ? (
        <>
          <div className='mb-6 grid grid-cols-1 min-[500px]:grid-cols-2 gap-4 pb-4 md:grid-cols-3 md:gap-10 lg:grid-cols-4 w-full'>
            {clubs.map((club: RenderedPlayerClubsData) => {
              const manageTermsClubLink = `${publicEnv.NEXT_PUBLIC_MANAGE_URL}/organizations/${club.slug || club.uuid}/terms-and-conditions`;
              const clubLink = `${publicEnv.NEXT_PUBLIC_PC_URL}/clubs/${club.slug || club.uuid}`;

              return (
                <CustomLink
                  key={`club-card-link-${club.uuid}`}
                  href={club.agreedToAllWaivers ? clubLink : manageTermsClubLink}
                  className='w-full'
                >
                  {FEATURE_FLAGS.ENABLE_NEW_CARDS ? (
                    <ClubCard
                      id={club.uuid}
                      key={`my-club-${club.uuid}`}
                      {...club}
                      location={formatLocation([club.city, club.state, club.country])}
                      logo={club.logo}
                      slug={club.slug || club.uuid}
                      memberCount={+club.memberCount}
                      title={club.title}
                      expired={isUserAlsoPlayer ? club.expired : undefined}
                      initialIsFavorite={club.isFavourite ?? false}
                      clubsAppBaseURL={publicEnv.NEXT_PUBLIC_PC_URL}
                      showFavoriteIcon={!!isUserAlsoPlayer || (!!user?.uuid && !isFavoritesTab)}
                      useFavoriteState={useClubFavorite}
                    />
                  ) : (
                    <MyClubCard
                      id={club.uuid}
                      location={formatLocation([club.city, club.state, club.country])}
                      logo={club.logo}
                      slug={club.slug || club.uuid}
                      memberCount={+club.memberCount}
                      title={club.title}
                      expired={isUserAlsoPlayer ? club.expired : undefined}
                      isFavourite={club.isFavourite}
                      isUserLoggedIn={!!user?.uuid}
                      showFavoriteIcon={!!isUserAlsoPlayer || (!!user?.uuid && !isFavoritesTab)}
                      isDisabled={isAdding || isRemoving}
                      handleFavoriteToggle={handleFavoriteToggle}
                    />
                  )}
                </CustomLink>
              );
            })}
          </div>

          {totalRecords > ALL_MODULES_PAGE_SIZE && (
            <PaginationV2
              totalNumberOfPages={Math.ceil(totalRecords / ALL_MODULES_PAGE_SIZE)}
              currentPage={page}
              onPreviousClick={() =>
                router.replace(`?club_type=${searchParamForClub}&page=${page - 1}`)
              }
              onNextClick={() =>
                router.replace(`?club_type=${searchParamForClub}&page=${page + 1}`)
              }
              onPageClick={(newPage) =>
                router.replace(`?club_type=${searchParamForClub}&page=${newPage}`)
              }
            />
          )}
        </>
      ) : !canManageClub && isManage ? (
        <AccessLimited />
      ) : (
        <div className='mx-auto max-w-[442px]'>
          <MyModulesNoResults {...noResultsConfig} />
        </div>
      )}
    </div>
  );
};
